import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { cn } from '../../lib/cn';
import { useControlled } from '../../lib/use-controlled';
import { CountBadge } from './count-badge';

export interface TabItem {
  value: string;
  label: string;
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Name der Tableiste für Screenreader. */
  label?: string;
  /** Inhalte als <TabsContent value="…">. */
  children?: ReactNode;
  className?: string;
}

/**
 * Abschnitte eines Datensatzes (Übersicht, Aktivitäten, Angebote), unterstrichen in highlight.
 *
 * Ist die Leiste breiter als ihr Platz (am Handy), scrollt sie seitlich. Damit
 * man das sieht, laufen die Reiter am Rand, hinter dem noch welche liegen, ins
 * Transparente aus (Maske, kein Farbverlauf: wirkt auf jeder Fläche), und der
 * aktive Reiter rückt in die Mitte, sodass sein Nachbar angeschnitten ist.
 * Bis Oktober 2026 war „Angebot“ im PM am Handy unsichtbar, ohne dass man es
 * ahnte (Gegenprüfung Preismodell, G-21).
 */
export function Tabs({ items, value, defaultValue, onChange, label, children, className }: TabsProps) {
  const [cur, set] = useControlled(value, defaultValue ?? items[0]?.value ?? '', onChange);
  const leiste = useRef<HTMLDivElement>(null);
  const [rand, setRand] = useState({ links: false, rechts: false });

  // Layout-Effekt: Maske und Mitte stehen schon im ersten Bild, ohne Sprung.
  useLayoutEffect(() => {
    const el = leiste.current;
    if (!el) return;
    const messen = () => {
      const links = el.scrollLeft > 1;
      const rechts = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setRand((r) => (r.links === links && r.rechts === rechts ? r : { links, rechts }));
    };
    messen();
    el.addEventListener('scroll', messen, { passive: true });
    // Die Leiste selbst und jeder Reiter: Werden Reiter breiter (Zahl geladen,
    // Schrift nachgeladen, längere Beschriftung), ändert sich die Breite der
    // Leiste nicht, wohl aber ihr Überlauf. jsdom kennt ResizeObserver nicht.
    const beobachter = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(messen);
    beobachter?.observe(el);
    for (const reiter of Array.from(el.children)) beobachter?.observe(reiter);
    return () => {
      el.removeEventListener('scroll', messen);
      beobachter?.disconnect();
    };
  }, [items]);

  // Nur waagerecht in der Leiste scrollen, nie die Seite: scrollIntoView
  // zöge Reiter weiter unten auf der Seite beim Laden ins Bild.
  const mittig = (tab: HTMLElement | null | undefined) => {
    const el = leiste.current;
    if (!el || !tab || el.scrollWidth <= el.clientWidth) return;
    el.scrollTo({ left: tab.offsetLeft - (el.clientWidth - tab.offsetWidth) / 2 });
  };

  useLayoutEffect(() => {
    mittig(leiste.current?.querySelector<HTMLElement>('[role="tab"][data-state="active"]'));
  }, [cur]);

  return (
    <TabsPrimitive.Root value={cur} onValueChange={set} className={className}>
      <TabsPrimitive.List
        ref={leiste}
        aria-label={label}
        className={cn(
          // relative: offsetLeft der Reiter zählt ab der Leiste.
          'relative flex gap-5 overflow-x-auto border-b border-border',
          rand.links && rand.rechts
            ? '[mask-image:linear-gradient(to_right,transparent,black_2.5rem,black_calc(100%-2.5rem),transparent)]'
            : rand.rechts
              ? '[mask-image:linear-gradient(to_right,black_calc(100%-2.5rem),transparent)]'
              : rand.links && '[mask-image:linear-gradient(to_right,transparent,black_2.5rem)]',
        )}
      >
        {items.map((t) => (
          <TabsPrimitive.Trigger
            key={t.value}
            value={t.value}
            // Auch per Tastatur fokussiert in die Mitte, nicht im ausgeblendeten Rand.
            onFocus={(e) => mittig(e.currentTarget)}
            className={cn(
              'relative inline-flex h-10 touch:h-11 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground data-[state=active]:text-foreground',
              'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-highlight after:opacity-0 data-[state=active]:after:opacity-100',
            )}
          >
            {t.label}
            {t.count != null && <CountBadge>{t.count}</CountBadge>}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>
      {children}
    </TabsPrimitive.Root>
  );
}

export function TabsContent({ className, ...rest }: TabsPrimitive.TabsContentProps) {
  return <TabsPrimitive.Content className={cn('pt-4 outline-none', className)} {...rest} />;
}
